
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Stack,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  TextField,
  Breadcrumbs,
  Link,
  Button,
  InputAdornment,
  TableSortLabel,
  Tooltip,

} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import SearchIcon from "@mui/icons-material/Search";
import { useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Resizable } from "re-resizable";
import LoadingSpinner from "../../Components/LoadingSpinner";

const
  TableComponent = ({
    FiltersJsx,
    columns,
    rows,
    CountryJsx,
    handleRowDoubleClick,
    handleSearchChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handleClick,
    handleHomeClick,
    isResident,
    handleLocationclick,
    filteredRows,
    title,
    titleMaster,
    handleIconClick,
    isLoading,
    pagination,
    disableSelection = true,
  }) => {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(1000);
    const location = useLocation();
    const permissions = location.state?.permissions || [];
    const [showFullText, setShowFullText] = useState({});
    const [orderBy, setOrderBy] = useState('');
    const [order, setOrder] = useState('asc');
    const [isExporting, setIsExporting] = useState(false);
    const handlePageChange = (newPage) => {
      setPage(newPage);
    };

    const handleLocationNavigation = () => {
      handleLocationclick(permissions);
    };

    const handleRequestSort = (property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    };
    const [columnWidths, setColumnWidths] = useState(() => {

      return columns.reduce((acc, column) => {
        acc[column.id] = 100; // Default width of 200px
        return acc;
      }, {});
    });

    const sortData = (data) => {
      if (!orderBy) return data;

      return [...data].sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];

        if (order === 'asc') {
          if (typeof aValue === 'number') return aValue - bValue;
          return String(aValue).localeCompare(String(bValue));
        } else {
          if (typeof aValue === 'number') return bValue - aValue;
          return String(bValue).localeCompare(String(aValue));
        }
      });
    };


    const truncateText = (text, maxLength = 20) => {
      if (typeof text !== 'string') return text;
      return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    const handleResize = (column, width) => {
      const newWidth = Math.max(width, 100);
      setColumnWidths(prev => ({
        ...prev,
        [column]: newWidth
      }));
    };
    const sortedAndPaginatedData = sortData(filteredRows)
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const totalPages = Math.ceil(filteredRows.length / rowsPerPage);


    const DynamicTableCell = ({ column, value }) => {
      const currentWidth = columnWidths[column.id] || 20;
      const shouldShowFullText = currentWidth > 10;

      // Check if this is a status column (month_year format)
      const isStatusColumn = /^\d+_\d+$/.test(column.id);

      if (isStatusColumn) {
        // Determine color based on status
        let statusColor;
        switch (value) {
          case 'G':
            statusColor = '#4CAF50'; // Green
            break;
          case 'P':
            statusColor = '#F44336'; // Red
            break;
          default:
            statusColor = '#9E9E9E'; // Grey for unknown status
        }

        return (
          <TableCell
            align={column.align || 'center'}
            sx={{
              width: 25, // Fixed width for month columns
              minWidth: 25,
              maxWidth: 25,
              padding: '1px',
              textAlign: 'center'
            }}
          >
            <Tooltip title={value} arrow placement="top">
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: statusColor,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '10px'
                }}
              >
                {value}
              </Box>
            </Tooltip>
          </TableCell>
        );
      }

      return (
        <TableCell
          align={column.align}
          sx={{
            width: currentWidth,
            minWidth: currentWidth,
            maxWidth: currentWidth,
            padding: '1px',
            '& .cell-content': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              display: 'block',
              transition: 'all 0.3s ease'
            }
          }}
        >
          <Tooltip title={value} arrow placement="top">
            <div className="cell-content">
              {value}
            </div>
          </Tooltip>
        </TableCell>
      );
    };

    return (
      <Box
        sx={{
          maxWidth: "95vw",
          overflowX: "hidden",
          backgroundColor: "#f9f9f9",
          minHeight: "90vh"
        }}
      >
        <ToastContainer />
        <Box
          sx={{
            backgroundColor: "#ffffff",
            padding: { xs: "8px 16px", sm: "12px 16px" },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
            borderRadius: "12px",
            margin: "10px auto",
            maxWidth: "95%",
            flexDirection: { xs: "column", sm: "column", md: "row" }
          }}
        >
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="›"
            sx={{
              fontSize: { xs: "14px", sm: "16px" },
              display: "flex",
              flexDirection: { xs: "column", sm: "column", md: "row" }
            }}
          >
            <Link
              onClick={handleHomeClick}
              underline="hover"
              color="#635bff"
              sx={{ cursor: "pointer", fontWeight: "500" }}
            >
              Home
            </Link>

            <Typography
              color="#635bff"
              onClick={handleLocationclick}
              sx={{ cursor: "pointer", fontWeight: "500" }}
            >
              {title}
            </Typography>
            <Typography color="text.primary" sx={{ fontWeight: "500" }}>
              {titleMaster}
            </Typography>
          </Breadcrumbs>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              gap: "16px",
              minWidth: "300px",
              flexDirection: { xs: "column", sm: "column", md: "row" },
              marginTop: { xs: "16px", sm: "16px", md: "0px" }
            }}
          >
            {FiltersJsx ? FiltersJsx : null}

          </Box>
        </Box>

        <Paper
          sx={{
            width: "95%",
            overflow: "hidden",
            margin: "10px auto",
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
            padding: "20px"
          }}
        >
          {isLoading ? (
            <Box sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              textAlign: "center",
              zIndex: 1000
            }}>
              <LoadingSpinner />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 450 }}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow sx={{ "& > th": { padding: "2px" } }}>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={{
                            backgroundColor: "#dddbff",
                            color: "#333",
                            fontWeight: "600",
                            padding: "1px 1px 1px 1px !important",
                            border: "0.5px solid #ddd",
                            borderBottom: "2px solid #ddd",
                            width: column.width || 'auto',

                          }}
                        >
                          <Resizable
                            size={{
                              width: columnWidths[column.id] || 28,
                              height: "100%"
                            }}
                            onResizeStop={(e, direction, ref, d) => {
                              handleResize(
                                column.id,
                                (columnWidths[column.id] || 28) + d.width
                              );
                            }}
                            enable={{ right: true }}
                            minWidth={28}
                            handleStyles={{
                              right: {
                                width: '8px',
                                right: '-4px',
                                cursor: 'col-resize'
                              }
                            }}
                          >

                            <Box>
                              <TableSortLabel
                                active={true}
                                direction={orderBy === column.id ? order : 'asc'}
                                onClick={() => handleRequestSort(column.id)}
                                sx={{
                                  '& .MuiTableSortLabel-icon': {
                                    opacity: '1 !important',
                                    visibility: 'visible !important'
                                  }
                                }}
                              >
                                <Typography variant="subtitle1" fontWeight="500">
                                  {column.label}
                                </Typography>
                              </TableSortLabel>

                              <TextField
                                size="small"
                                variant="outlined"
                                placeholder={`Search ${column.label}`}
                                onChange={(e) => handleSearchChange(column.id, e.target.value)}
                                sx={{
                                  mt: 1,
                                  width: '100%',
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    backgroundColor: "#f9f9f9",
                                    fontSize: "0.75rem",
                                    height: "26px",
                                  },
                                }}
                              />
                            </Box>
                          </Resizable>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedAndPaginatedData.map((row, index) => (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={index}
                        onDoubleClick={() => handleRowDoubleClick(row)}
                        sx={{
                          "& > td": { padding: "0.2px", border: "0.5px solid #ddd" },
                          "&:hover": { backgroundColor: "#f1f1f1", cursor: "pointer" },
                          transition: "background-color 0.3s ease",
                        }}
                      >
                        {CountryJsx && <CountryJsx row={row} ind={index} />}
                        {(CountryJsx ? columns.slice(1) : columns).map((column) => (
                          <DynamicTableCell
                            key={column.id}
                            column={column}
                            value={column.id === "Index" ? (index + 1) : row[column.id]}
                          />
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[500,1000, 3000, 5000, 10000]}
                component="div"
                count={pagination?.totalRecords || filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => {
                  handleChangePage(event, newPage);
                }}
                onRowsPerPageChange={(event) => {
                  handleChangeRowsPerPage(event);
                }}
                sx={{
                  backgroundColor: "#dddbff",
                  borderTop: "1px solid #ddd",
                  color: "#333",
                  borderRadius: "0 0 12px 12px",
                  "& .MuiToolbar-root": {
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    width: "100%",

                    flexWrap: "wrap",
                  },
                  "& .MuiTablePagination-displayedRows": {
                    display: "none",
                  },
                  "& .MuiTablePagination-actions": {
                    display: "none",
                  },

                  "& .MuiTablePagination-selectLabel": {
                    marginBottom: 2,
                  },
                  "& .MuiTablePagination-select": {
                    marginBottom: 0,
                  },
                }}
                ActionsComponent={({ onPageChange }) => (
                  <Stack
                    // spacing={2}
                    direction="row"
                    alignItems="center"
                    sx={{
                      // flex: '1',
                      justifyContent: "center",
                      marginLeft: "16px",
                    }}
                  >
                    <Typography variant="body2" sx={{ position: 'absolute', left: '20px', fontWeight: 'bold', color: '#333' }}>
                      Total Count: {filteredRows.length}
                    </Typography>
                    <Pagination
                      count={pagination?.totalPages || totalPages}
                      page={pagination?.currentPage || page + 1}
                      onChange={(event, newPage) => {
                        handleChangePage(null, newPage - 1);
                      }}
                      showFirstButton
                      showLastButton
                      sx={{
                        "& .MuiPaginationItem-root.Mui-selected": {
                          backgroundColor: "#635bff",
                          color: "#ffffff",
                        },
                      }}
                    />
                  </Stack>
                )}
              />
            </>
          )}
        </Paper>
      </Box>
    );
  };

export default TableComponent;

